/**
 * Route Guard Component
 * Conditionally renders routes based on user type (super admin vs regular user),
 * the landing path the role is pinned to, and per-route RBAC subject declared
 * via React Router `handle: { subject, require }`.
 */

import { Navigate, Outlet, useLocation, useMatches } from "react-router";
import { useAuth, isSuperAdmin } from "@/hooks/use-auth";
import { useAccountFlags } from "@/hooks/use-account-flags";
import { usePermissions } from "@/hooks/use-permissions";
import { Spinner } from "@/components/ui/spinner";
import { DEFAULT_LANDING_PATH, getLandingPath } from "@/lib/landing";

interface RouteHandle {
  subject?: string;
  require?: "view" | "edit";
  /**
   * Administrators only, whatever the subject grants: a page a role's permission would open
   * but the role must not use (a Sentinel holds sentinel-workspace edit, yet must not change
   * Sentinel settings).
   */
  adminOnly?: boolean;
  /**
   * Account feature flag the route requires.
   *
   * Hiding the nav entry is not enough on its own — the URL is still typeable, and a bookmark
   * from before the feature was switched off still resolves.
   */
  flag?: string;
}

/**
 * Shown when a role's own landing page denies it. Navigating there would bounce
 * straight back, so say so instead of spinning between two redirects.
 */
function NoAccessibleRoutes() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-2 px-6 text-center">
      <p className="text-base font-medium">No pages are available for your role</p>
      <p className="text-sm text-muted-foreground">
        Ask your administrator to grant access to this workspace.
      </p>
    </div>
  );
}

export default function RouteGuard() {
  const { currentUser, isAuthenticated } = useAuth();
  const { can, role, isSuperAdmin: rbacSuperAdmin, isLoading } = usePermissions();
  const { pathname } = useLocation();
  const matches = useMatches();
  const { flags, isLoading: flagsLoading } = useAccountFlags();

  if (isAuthenticated === undefined || !currentUser || isLoading || flagsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner className="size-8" />
      </div>
    );
  }

  const userIsSuperAdmin = isSuperAdmin(currentUser) || rbacSuperAdmin;

  if (userIsSuperAdmin && !pathname.startsWith("/super-admin")) {
    return <Navigate to="/super-admin/accounts" replace />;
  }

  if (!userIsSuperAdmin && pathname.startsWith("/super-admin")) {
    return <Navigate to={DEFAULT_LANDING_PATH} replace />;
  }

  if (userIsSuperAdmin) {
    return <Outlet />;
  }

  const landingPath = getLandingPath(role);

  // The index route carries no `handle`, so this role rule is the only thing
  // that can move a dashboard-scoped user off the home page.
  if (landingPath !== DEFAULT_LANDING_PATH && pathname === DEFAULT_LANDING_PATH) {
    return <Navigate to={landingPath} replace />;
  }

  // Per-route RBAC enforcement via `handle: { subject, require }`
  // Checked before RBAC because it is the blunter question: permissions decide whether this role
  // may see a page, the flag decides whether the page exists for this account at all. Super admins
  // never reach here — they are redirected into /super-admin above.
  const flagged = [...matches]
    .reverse()
    .find((m) => (m.handle as RouteHandle | undefined)?.flag);
  const requiredFlag = (flagged?.handle as RouteHandle | undefined)?.flag;
  if (requiredFlag && flags[requiredFlag] !== true) {
    return <Navigate to={landingPath} replace />;
  }

  const adminOnly = matches.some((m) => (m.handle as RouteHandle | undefined)?.adminOnly);
  if (adminOnly && role !== "administrator") {
    return <Navigate to={landingPath} replace />;
  }

  const gated = [...matches]
    .reverse()
    .find((m) => (m.handle as RouteHandle | undefined)?.subject);
  const handle = gated?.handle as RouteHandle | undefined;
  if (handle?.subject && !can(handle.require ?? "view", handle.subject)) {
    // Denied on the landing page itself (permissions not seeded yet, say) —
    // redirecting there would loop.
    if (pathname === landingPath) return <NoAccessibleRoutes />;
    return <Navigate to={landingPath} replace />;
  }

  return <Outlet />;
}
